import { defineStore } from "pinia"
import { ref, computed } from "vue"

export interface ChatMessage {
    id: string
    role: "user" | "assistant" | "system"
    content: string
    action?: { type: string; inputType: string }
    timestamp: number
}

export type ChatbotStatus = "idle" | "analyzing" | "executing" | "paused" | "complete" | "error"

export const useChatbotStore = defineStore("chatbot", () => {
    const isOpen = ref(false)
    const status = ref<ChatbotStatus>("idle")
    const useCase = ref("")
    const messages = ref<ChatMessage[]>([])
    const executedActions = ref<string[]>([])
    const error = ref<string | null>(null)
    const shouldStop = ref(false)

    const isRunning = computed(() => status.value === "analyzing" || status.value === "executing")

    function togglePanel() {
        isOpen.value = !isOpen.value
    }

    function openPanel() {
        isOpen.value = true
    }

    function closePanel() {
        isOpen.value = false
    }

    function setStatus(newStatus: ChatbotStatus) {
        status.value = newStatus
    }

    function addMessage(role: ChatMessage["role"], content: string, action?: { type: string; inputType: string }) {
        messages.value.push({
            id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
            role,
            content,
            action,
            timestamp: Date.now()
        })
    }

    function addExecutedAction(description: string) {
        executedActions.value.push(description)
    }

    function setError(errorMessage: string | null) {
        error.value = errorMessage
    }

    function requestStop() {
        shouldStop.value = true
    }

    function clearStopRequest() {
        shouldStop.value = false
    }

    function reset() {
        status.value = "idle"
        useCase.value = ""
        messages.value = []
        executedActions.value = []
        error.value = null
        shouldStop.value = false
    }

    return {
        isOpen,
        status,
        useCase,
        messages,
        executedActions,
        error,
        shouldStop,
        isRunning,
        togglePanel,
        openPanel,
        closePanel,
        setStatus,
        addMessage,
        addExecutedAction,
        setError,
        requestStop,
        clearStopRequest,
        reset
    }
})
